import { useMemo, useState, useEffect, useRef } from "react";
import { logApiError, logUnexpectedError } from "../Utils/apiError";

const LS_NAME = "meshum_buyer_name";
const LS_EMAIL = "meshum_buyer_email";

function readStoredBuyer() {
  try {
    return {
      name: localStorage.getItem(LS_NAME) || "",
      email: localStorage.getItem(LS_EMAIL) || "",
    };
  } catch {
    return { name: "", email: "" };
  }
}

function writeStoredBuyer({ name, email }) {
  try {
    if (name) localStorage.setItem(LS_NAME, name);
    if (email) localStorage.setItem(LS_EMAIL, email);
  } catch {
    /* ignore */
  }
}

export default function ContactAtMestimate({
  dealerId,
  carId,
  year,
  make,
  model,
  mestimate,
  askingPrice,
  defaultBuyer,
  defaultMessage = "",
}) {
  const API = process.env.REACT_APP_API_BASE;

  const [open, setOpen] = useState(false);
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [buyerPhone, setBuyerPhone] = useState("");
  const [message, setMessage] = useState("");
  const [offer, setOffer] = useState("");
  const [loading, setLoading] = useState(false);
  const [okMsg, setOkMsg] = useState("");
  const [errMsg, setErrMsg] = useState("");

  const MAX_MESSAGE_LENGTH = 500;
  const nameInputRef = useRef(null);
  const modalRef = useRef(null);

  const defaultOfferNumber = useMemo(() => {
    const m = Number.isFinite(mestimate) ? Math.round(mestimate) : null;
    const a = Number.isFinite(askingPrice) ? Math.round(askingPrice) : null;
    if (m != null && a != null) return Math.min(m, a);
    return m ?? a ?? null;
  }, [mestimate, askingPrice]);

  const starter = useMemo(() => {
    if (defaultMessage?.trim()) return defaultMessage.trim();
    const title = [year, make, model].filter(Boolean).join(" ");
    const mestText = Number.isFinite(mestimate)
      ? ` at ${currency(mestimate)}`
      : "";
    return title ? `I'm interested in the ${title}${mestText}.` : `I'm interested in this vehicle${mestText}.`;
  }, [defaultMessage, year, make, model, mestimate]);

  const hydrateBuyerFields = () => {
    const stored = readStoredBuyer();

    const authFirst = defaultBuyer?.firstName || "";
    const authLast = defaultBuyer?.lastName || "";
    const authName =
      (defaultBuyer?.name && defaultBuyer.name.trim()) ||
      [authFirst, authLast].filter(Boolean).join(" ").trim();

    const authEmail = defaultBuyer?.email || "";
    const authPhone = defaultBuyer?.phone || "";

    setBuyerName(authName || stored.name || "");
    setBuyerEmail(authEmail || stored.email || "");
    setBuyerPhone(authPhone || "");
  };

  const resetForm = () => {
    hydrateBuyerFields();
    setMessage(starter);
    setOffer(
      defaultOfferNumber != null
        ? defaultOfferNumber.toLocaleString("en-US", { maximumFractionDigits: 0 })
        : ""
    );
    setErrMsg("");
    setOkMsg("");
    setLoading(false);
  };

  useEffect(() => {
    resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    carId,
    starter,
    defaultOfferNumber,
    defaultBuyer?.name,
    defaultBuyer?.email,
    defaultBuyer?.firstName,
    defaultBuyer?.lastName,
    defaultBuyer?.phone,
  ]);

  useEffect(() => {
    if (!message?.trim()) setMessage(starter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [starter]);

  useEffect(() => {
    if (!open) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    hydrateBuyerFields();

    if (!message?.trim()) setMessage(starter);
    if (!offer?.trim() && defaultOfferNumber != null) {
      setOffer(defaultOfferNumber.toLocaleString("en-US", { maximumFractionDigits: 0 }));
    }

    setTimeout(() => nameInputRef.current?.focus(), 0);

    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const validate = () => {
    if (!dealerId) {
      setErrMsg("Dealer not available.");
      return false;
    }
    if (!buyerEmail && !buyerPhone) {
      setErrMsg("Provide an email or phone.");
      return false;
    }
    if (buyerEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(buyerEmail)) {
        setErrMsg("Please enter a valid email address.");
        return false;
      }
    }
    if (buyerPhone) {
      const digits = buyerPhone.replace(/\D/g, "");
      if (digits.length < 10 || digits.length > 15) {
        setErrMsg("Please enter a valid phone number (10-15 digits).");
        return false;
      }
    }
    if (message.length > MAX_MESSAGE_LENGTH) {
      setErrMsg(`Message must be under ${MAX_MESSAGE_LENGTH} characters.`);
      return false;
    }
    if (offer?.trim()) {
      const raw = offer.replace(/\D/g, "");
      const num = Number(raw);
      if (!Number.isFinite(num) || num <= 0 || num > 2000000) {
        setErrMsg("Please enter a valid offer amount.");
        return false;
      }
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setErrMsg("");
    setOkMsg("");

    if (!validate()) return;

    const cleanPhone = buyerPhone ? buyerPhone.replace(/\D/g, "") : null;
    const offerNumber = offer?.trim() ? parseInt(offer.replace(/\D/g, ""), 10) : null;

    setLoading(true);
    try {
      const res = await fetch(`${API}/public/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dealerId,
          carId: carId ?? null,
          buyerEmail: buyerEmail || null,
          buyerPhone: cleanPhone || null,
          buyerName: buyerName?.trim() || null,
          message: message && message.trim() ? message : starter,
          mestimate: offerNumber,
        }),
      });
      if (!res.ok) {
        await logApiError(res, "Lead contact form failed");
        throw new Error("Lead contact form failed");
      }

      writeStoredBuyer({
        name: buyerName?.trim(),
        email: buyerEmail?.trim(),
      });

      window.gtag?.("event", "contact_mestimate_submit", {
        car_id: carId,
        dealer_id: dealerId,
        mestimate: offerNumber ?? null,
        contact_channel: buyerEmail ? (buyerPhone ? "email+phone" : "email") : "phone",
      });
      window.gtag?.("event", "generate_lead", {
        carId,
        dealerId,
        hasEmail: Boolean(buyerEmail),
        hasPhone: Boolean(cleanPhone),
        hasOffer: offerNumber != null,
        source: "contact_mestimate",
      });
      window.fbq?.("track", "Lead", { value: offerNumber ?? 0, currency: "USD" });

      setOkMsg("Message sent. The dealer will contact you soon.");
      setTimeout(() => {
        setOpen(false);
        resetForm();
      }, 900);
    } catch (err) {
      logUnexpectedError(err, "Lead contact request failed");
      setErrMsg("Could not send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onOverlayClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      setOpen(false);
    }
  };

  return (
    <>
      <div className="space-y-2">
        <button
          onClick={() => {
            window.gtag?.("event", "contact_mestimate_open", { car_id: carId, dealer_id: dealerId });
            window.fbq?.("trackCustom", "ContactMestimateOpen", { car_id: carId, dealer_id: dealerId });
            setOpen(true);
          }}
          className="w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
        >
          Make offer
        </button>
        <p className="text-xs text-slate-500">
          Message the dealer with a price guided by Meshum Estimate.
        </p>
      </div>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="contact-mestimate-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onMouseDown={onOverlayClick}
        >
          <div
            ref={modalRef}
            className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 id="contact-mestimate-title" className="text-lg font-semibold text-slate-900">
                    Contact dealer
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Send a focused message and use Meshum pricing context to guide the offer.
                  </p>
                </div>
                <button
                  aria-label="Close"
                  onClick={() => setOpen(false)}
                  className="rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                >
                  &times;
                </button>
              </div>
            </div>

            <div className="max-h-[85vh] overflow-y-auto px-5 py-4">
              {okMsg && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                  {okMsg}
                </div>
              )}
              {errMsg && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {errMsg}
                </div>
              )}

              <form onSubmit={submit} className="mt-4 space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Your name">
                    <input
                      ref={nameInputRef}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2.5 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      value={buyerName}
                      onChange={(e) => setBuyerName(e.target.value)}
                      placeholder="Jane Doe"
                      autoComplete="name"
                    />
                  </Field>
                  <Field label="Email">
                    <input
                      type="email"
                      className="w-full rounded-xl border border-slate-300 px-3 py-2.5 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      value={buyerEmail}
                      onChange={(e) => setBuyerEmail(e.target.value)}
                      placeholder="you@example.com"
                      autoComplete="email"
                    />
                  </Field>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Phone">
                    <input
                      className="w-full rounded-xl border border-slate-300 px-3 py-2.5 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      value={buyerPhone}
                      onChange={(e) => setBuyerPhone(e.target.value)}
                      placeholder="(555) 123-4567"
                      autoComplete="tel"
                    />
                  </Field>
                  <Field label="Offer amount">
                    <div className="relative">
                      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-500">$</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="w-full rounded-xl border border-slate-300 py-2.5 pl-7 pr-3 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        value={offer}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/\D/g, "");
                          setOffer(raw ? parseInt(raw, 10).toLocaleString("en-US", { maximumFractionDigits: 0 }) : "");
                        }}
                        placeholder={
                          defaultOfferNumber != null
                            ? defaultOfferNumber.toLocaleString("en-US", { maximumFractionDigits: 0 })
                            : "e.g., 27,500"
                        }
                      />
                    </div>
                  </Field>
                </div>

                <div>
                  <Field label="Message">
                    <textarea
                      className="min-h-28 w-full rounded-xl border border-slate-300 px-3 py-2.5 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      rows={4}
                      maxLength={MAX_MESSAGE_LENGTH}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Hi! Is this still available?"
                    />
                  </Field>
                  <div className="mt-1 text-xs text-slate-500">
                    {message.length}/{MAX_MESSAGE_LENGTH} characters
                  </div>
                </div>

                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="h-11 rounded-xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="h-11 rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loading ? "Sending..." : "Send message"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}

function currency(n) {
  const value = Number(n);
  if (!Number.isFinite(value)) return "";
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}
